import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	DestroyRef,
	inject,
	OnInit,
	ViewChild
} from "@angular/core";
import { CdkTrapFocus } from "@angular/cdk/a11y";
import { UniversalModalModule } from "@modules/+new/shared/universal-modal/universal-modal.module";
import { UniversalModalService } from "@modules/+new/shared/universal-modal/universal-modal.service";
import { ModalCloseReason } from "@modules/+new/shared/universal-modal/entities/modal-close-reason.enum";
import { BpSwitchModule } from "@modules/core/components/bp-switch/bp-switch.module";
import { StakingOverviewFacade } from "@modules/staking/domain/+state/staking-overview.facade";
import { CoreModule } from "@modules/core/core.module";
import { StakingOverviewStateService } from "@modules/staking/domain/+state/staking-overview.state.service";
import { StakingOverviewRemote } from "@modules/staking/domain/infrastructure/staking-overview.remote";
import { combineLatest, map, Observable, of, switchMap, take, tap } from "rxjs";
import { AutoStakingFacade } from "@modules/staking/domain/+state/auto-staking.facade";
import { Helper } from "@helpers/functions";
import { AUTO_STAKING_OVERVIEW_WIDGET_DISMISSED } from "@helpers/constants";
import { TermsAndConditionsService } from "@modules/shared/terms-and-conditions/services/terms-and-conditions.service";
import { AgreementBundle, UserJourney } from "@modules/shared/terms-and-conditions/entities/terms-and-conditions.model";
import { StakingOverviewState } from "@modules/staking/domain/entities/staking-overview-state.model";
import { TermsAndConditionsUpdatesModule } from "@modules/shared/terms-and-conditions/components/terms-and-conditions-updates.module";
import {
	DYNAMIC_TERMS_TOKEN,
	TermsAndConditionsUpdatesComponent
} from "@modules/shared/terms-and-conditions/components/terms-and-conditions-updates.component";
import { BpTryAgainErrorModule } from "@v4-components/bp-try-again-error/bp-try-again-error.module";
import { InfoService } from "@core-services/info.service";
import { TranslationService } from "@core-services/translation.service";
import { GetStakingUserJourneyQuery } from "../../../../api/graphql/staking/generated/auto-staking.user-journey.service.graphql-gen";
import { LaunchDarklyService, LD_FLAG } from "@core-services/launch-darkly/launch-darkly.service";
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { ToggleInputModule } from "@modules/shared/bp-toggle/toggle-input.module";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { UserService } from "@main-services/user.service";
import { AutoStakingTrackingFacade } from "@modules/staking/domain/+state/auto-staking-tracking.facade";
import { NewProfileFacade } from "@modules/main/components/profile/new-profile/state/new-profile.facade";
import { catchError } from "rxjs/operators";
import { WEB3_MERGE_PHASE, Web3Facade } from "@modules/web3/+state/web3.facade";

@Component({
	selector: "auto-staking-confirmation",
	imports: [
		CdkTrapFocus,
		UniversalModalModule,
		BpSwitchModule,
		CoreModule,
		TermsAndConditionsUpdatesModule,
		BpTryAgainErrorModule,
		ReactiveFormsModule,
		ToggleInputModule
	],
	providers: [StakingOverviewFacade, StakingOverviewStateService, StakingOverviewRemote],
	templateUrl: "./auto-staking-confirmation.component.html",
	styleUrl: "./auto-staking-confirmation.component.scss",
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class AutoStakingConfirmationComponent implements OnInit {
	@ViewChild(DYNAMIC_TERMS_TOKEN) dynamic_terms: TermsAndConditionsUpdatesComponent;

	private readonly universalModalService = inject(UniversalModalService);
	private readonly stakingRemote = inject(StakingOverviewFacade);
	private readonly autoStakingFacade = inject(AutoStakingFacade);
	private readonly termsAndConditionsService = inject(TermsAndConditionsService);
	private readonly cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
	private readonly infoService = inject(InfoService);
	private readonly translationService = inject(TranslationService);
	private readonly launchDarklyService = inject(LaunchDarklyService);
	private readonly profileService = inject(NewProfileFacade);
	private readonly userService = inject(UserService);
	private readonly fb = inject(FormBuilder);
	private destroyRef = inject(DestroyRef);
	private readonly autoStakingTrackingFacade = inject(AutoStakingTrackingFacade);
	private readonly web3Facade = inject(Web3Facade);
	web3Phase$ = this.web3Facade.getMergePhase();

	public termsAccepted = false;
	public termsError = false;
	public apiLoading = false;
	public disableButton = false;

	termsForm: FormGroup;

	private bundles: AgreementBundle[] = [];
	userJourneys = [];

	public vm$: Observable<{ overViewState: StakingOverviewState; agreementBundles: AgreementBundle[] }> =
		this.getVm$().pipe(tap(({ overViewState, agreementBundles }) => (this.bundles = agreementBundles)));

	readonly isDynamictermAndConditionActive$ = this.launchDarklyService.isActive$(LD_FLAG.TERMS_AND_CONDITIONS);

	defaultLogo: string = "/assets/images/default-asset.svg";
	public hasLockedAssets: boolean = false;
	public countryIso = "";

	public ngOnInit(): void {
		this.termsForm = this.fb.group({
			autoStaking: false
		});

		this.autoStakingTrackingFacade.trackEvent("Auto Staking Setting Screen Viewed");

		this.isDynamictermAndConditionActive$
			.pipe(
				tap((isEnabled) => {
					if (!isEnabled) {
						this.disableButton = true;
						this.setValidators();
						this.cdr.markForCheck();
						this.termsForm.statusChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((status) => {
							this.disableButton = status !== "VALID";
						});
						this.profileService
							.getProfile$()
							.pipe(take(1))
							.subscribe((profile) => {
								this.countryIso = profile.iso;
							});
					}
				})
			)
			.subscribe();
	}

	private setValidators(): void {
		this.termsForm.controls.autoStaking.setValidators([Validators.requiredTrue]);
		this.termsForm.controls.autoStaking.updateValueAndValidity();
	}

	closeModal(): void {
		this.universalModalService.close(ModalCloseReason.close);
	}

	public getTermLink(): string {
		const regulatory_hub = this.countryIso === "DE" ? "bam-gmbh" : "bitpanda-gmbh";
		const language = this.userService.user.getLanguage() === "deu" ? "de" : "en";
		return `https://cdn.bitpanda.com/terms-and-conditions/staking-bitpanda-${regulatory_hub}-${language}-1.0.0.pdf`;
	}

	public continue(): void {
		if (this.bundles.length > 0) {
			this.confirm();
			return;
		}
		this.optIn();
	}

	private optIn(): void {
		this.apiLoading = true;
		this.autoStakingFacade.setAutoStakingState(true).subscribe({
			next: () => {
				this.apiLoading = false;
				this.cdr.markForCheck();
				this.autoStakingTrackingFacade.trackEvent("Auto Staking Setting Changed ", { opt_in: true });
				this.infoService.setSuccess(this.translationService.translate("auto_staking_success_toast"));
				// Once opted in, dismiss the widget
				Helper.setCookie(AUTO_STAKING_OVERVIEW_WIDGET_DISMISSED, "true", true);

				this.closeModal();
			},
			error: () => {
				this.apiLoading = false;
				this.cdr.markForCheck();
			}
		});
	}

	private confirm(): void {
		this.termsAndConditionsService.acceptTerms(
			this.userJourneys,
			this.dynamic_terms?.termsAndConditionsForm?.value,
			() => {
				this.optIn();
			}
		);
	}

	disableFormCheck(): boolean {
		return this.dynamic_terms ? this.dynamic_terms?.termsAndConditionsForm?.invalid : false;
	}

	private getVm$(): Observable<{ overViewState: StakingOverviewState; agreementBundles: AgreementBundle[] }> {
		this.termsError = false;
		return combineLatest({
			overViewState: this.stakingRemote.getState$(),
			userJourney: this.autoStakingFacade.getStakingUserJourney$()
		}).pipe(
			switchMap(({ overViewState, userJourney }) => {
				let agreementBundles: Observable<AgreementBundle[]> = of([]);
				if (userJourney !== "error") {
					const journeyResponse = userJourney as GetStakingUserJourneyQuery;
					this.userJourneys = journeyResponse.settings.userJourneys.autoStaking as UserJourney[];
					agreementBundles = this.termsAndConditionsService.getAgreementBundleData$(this.userJourneys, true);
				} else {
					this.termsError = true;
				}
				return combineLatest({ overViewState: of(overViewState), agreementBundles }).pipe(
					map(({ overViewState, agreementBundles }) => ({ overViewState, agreementBundles }))
				);
			}),
			tap(({ overViewState, agreementBundles }) => {
				//reset toggles for dynamic terms
				if (this.dynamic_terms) {
					this.dynamic_terms.resetToggles();
				}
				this.hasLockedAssets = overViewState.autoStakeAssets.some((asset) => asset.unBoundPeriod > 0);
			}),
			catchError((error) => {
				this.termsError = true;
				return of({ overViewState: undefined, agreementBundles: [] });
			})
		);
	}

	refetchVm(): void {
		this.vm$ = this.getVm$();
	}

	triggerChangeHandler(): void {
		/**
		 * this is the caveat of change detection in angular
		 * for some reason this needs to be triggered
		 * otherwise the form status is not updated
		 * due to delay waiting the VM to resolve
		 */
		setTimeout(() => {
			this.cdr.markForCheck();
		}, 100);
	}

	protected readonly WEB3_MERGE_PHASE = WEB3_MERGE_PHASE;
}
